import { Tooltip } from "./feedback";
import { IconDeviceDesktop, IconMoon, IconSun } from "@tabler/icons-react";
import { useState, type CSSProperties } from "react";

/** "system" follows the operating system; "light" and "dark" override it. */
export type Theme = "system" | "light" | "dark";

const KEY = "theme";
const NEXT: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };
const LABEL: Record<Theme, string> = { system: "Theme: system", light: "Theme: light", dark: "Theme: dark" };

function stored(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

/** Sets `data-theme` on <html>, which styles.css reads; "system" removes it. */
function apply(theme: Theme) {
  if (theme === "system") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", theme);
}

// Applied on import, before the first render, so the page does not flash the other theme.
if (typeof document !== "undefined") apply(stored());

/** Button that cycles system → light → dark, remembered in this browser. */
export function ThemeToggle({ style }: { style?: CSSProperties }) {
  const [theme, setTheme] = useState<Theme>(stored);
  const change = () => {
    const next = NEXT[theme];
    try {
      if (next === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // Storage blocked: the choice lasts until the page is reloaded.
    }
    apply(next);
    setTheme(next);
  };
  const Icon = theme === "light" ? IconSun : theme === "dark" ? IconMoon : IconDeviceDesktop;
  return (
    <Tooltip content={`${LABEL[theme]} — click to change`}>
      <button type="button" className="ghost icon" style={style} onClick={change} aria-label={`${LABEL[theme]} (change)`}>
        <Icon size={18} />
      </button>
    </Tooltip>
  );
}
